import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import ClientShell from "@/components/ClientShell";
import StatusChip from "@/components/StatusChip";
import { MagnifyingGlass, ArrowUpRight, Package, Buildings, ArrowLeft, CaretRight } from "@phosphor-icons/react";
import AuthImage from "@/components/AuthImage";
import ShareLinkButton from "@/components/ShareLinkButton";
import { trackingUrl } from "@/lib/publicUrl";
import { useCategories } from "@/hooks/useCategories";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatDate, formatCurrency } from "@/lib/format";

const STATUS_OPTIONS = [
  { key: "all", label: "All statuses" },
  { key: "ORDER_RECEIVED", label: "Order Received" },
  { key: "RAW_MATERIALS", label: "Raw Materials" },
  { key: "IN_PRODUCTION", label: "In Production" },
  { key: "QUALITY_TESTING", label: "Quality Testing" },
  { key: "READY_FOR_SHIPMENT", label: "Ready for Shipment" },
  { key: "SHIPPED", label: "Shipped" },
  { key: "DELIVERED", label: "Delivered" },
];

const IN_PROD_STATUSES = ["RAW_MATERIALS", "IN_PRODUCTION", "QUALITY_TESTING"];

const projectKey = (o) => o.project_name || o.product_type || "Other";

export default function ActiveOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedProject = searchParams.get("project");

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/orders");
      setOrders(res.data);
    } catch (e) {
      console.error("[ActiveOrders] failed:", e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const projects = useMemo(() => {
    const map = new Map();
    for (const o of orders) {
      const key = projectKey(o);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(o);
    }
    return Array.from(map.entries()).map(([name, items]) => ({
      name,
      items,
      active: items.filter((o) => o.current_status !== "DELIVERED").length,
      inProd: items.filter((o) => IN_PROD_STATUSES.includes(o.current_status)).length,
      delivered: items.filter((o) => o.current_status === "DELIVERED").length,
      total: items.reduce((s, o) => s + (o.total_price || 0), 0),
      latest: items.reduce((d, o) => (o.updated_at > d ? o.updated_at : d), ""),
    }));
  }, [orders]);

  if (selectedProject) {
    const project = projects.find((p) => p.name === selectedProject);
    return (
      <ProjectDetail
        loading={loading}
        project={project}
        onBack={() => setSearchParams({})}
      />
    );
  }

  return (
    <ClientShell>
      <div className="mb-8">
        <div className="mt-tiny text-[#FF5A1F]">Active orders</div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight text-zinc-900 mt-2">
          Your projects.
        </h1>
        <p className="mt-2 text-zinc-600 text-sm">
          Tap a project to see the materials ordered for it and track each one.
        </p>
      </div>

      {loading ? (
        <div className="grid gap-3">
          {["s-1", "s-2", "s-3"].map((k) => (
            <div key={k} className="h-28 bg-white border border-zinc-200 animate-pulse" />
          ))}
        </div>
      ) : projects.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-14 text-center">
          <Buildings size={44} className="mx-auto text-zinc-300" weight="duotone" />
          <div className="mt-3 text-zinc-500 text-sm">No orders yet.</div>
        </div>
      ) : (
        <div className="grid gap-3" data-testid="projects-list">
          {projects.map((p) => (
            <button
              key={p.name}
              onClick={() => setSearchParams({ project: p.name })}
              data-testid={`project-card-${p.name}`}
              className="group text-left bg-white border border-zinc-200 hover:border-zinc-900 p-5 md:p-6 transition-all duration-200 hover:-translate-y-0.5"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#0047AB]/10 text-[#0047AB] grid place-items-center shrink-0">
                  <Buildings size={22} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="mt-tiny text-zinc-400">Project</div>
                  <div className="font-display text-lg md:text-xl font-bold text-zinc-900 mt-0.5 group-hover:text-[#0047AB] transition-colors truncate">
                    {p.name}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500">
                    <span><b className="text-zinc-900">{p.items.length}</b> material{p.items.length > 1 ? "s" : ""}</span>
                    {p.active > 0 && <span><b className="text-[#FF5A1F]">{p.active}</b> active</span>}
                    {p.delivered > 0 && <span><b className="text-green-700">{p.delivered}</b> delivered</span>}
                    <span className="font-mono-mt">{formatCurrency(p.total)}</span>
                  </div>
                </div>
                <CaretRight size={20} className="text-zinc-400 group-hover:text-[#FF5A1F] group-hover:translate-x-0.5 transition-transform shrink-0" />
              </div>
            </button>
          ))}
        </div>
      )}
    </ClientShell>
  );
}

function ProjectDetail({ loading, project, onBack }) {
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  if (loading) {
    return (
      <ClientShell>
        <div className="h-96 bg-white border border-zinc-200 animate-pulse" />
      </ClientShell>
    );
  }

  if (!project) {
    return (
      <ClientShell>
        <div className="bg-white border border-zinc-200 p-14 text-center">
          <div className="text-zinc-500">Project not found</div>
          <button onClick={onBack} className="mt-4 underline">Back to projects</button>
        </div>
      </ClientShell>
    );
  }

  const filtered = project.items.filter((o) => {
    if (statusFilter !== "all" && o.current_status !== statusFilter) return false;
    if (q) {
      const s = q.toLowerCase();
      if (
        !(o.order_number || "").toLowerCase().includes(s) &&
        !(o.product_name || "").toLowerCase().includes(s) &&
        !(o.product_type || "").toLowerCase().includes(s)
      ) return false;
    }
    return true;
  });

  return (
    <ClientShell>
      <button
        onClick={onBack}
        data-testid="project-back-button"
        className="inline-flex items-center gap-2 mt-tiny text-zinc-500 hover:text-zinc-900 mb-6 transition-colors"
      >
        <ArrowLeft size={14} /> Back to projects
      </button>

      <div className="mb-8 flex items-start gap-4">
        <div className="w-14 h-14 bg-[#0047AB]/10 text-[#0047AB] grid place-items-center shrink-0">
          <Buildings size={26} weight="duotone" />
        </div>
        <div className="min-w-0">
          <div className="mt-tiny text-[#FF5A1F]">Project</div>
          <h1 className="font-display text-2xl md:text-3xl font-black tracking-tight text-zinc-900 mt-1 break-words">
            {project.name}
          </h1>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500">
            <span><b className="text-zinc-900">{project.items.length}</b> materials</span>
            <span><b className="text-[#FF5A1F]">{project.active}</b> active</span>
            <span><b className="text-green-700">{project.delivered}</b> delivered</span>
            <span className="font-mono-mt">{formatCurrency(project.total)}</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            data-testid="order-search-input"
            placeholder="Search material or order number..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-white border border-zinc-200 focus:border-zinc-900 outline-none transition-colors"
          />
        </div>
        <div className="w-full md:w-64">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="status-filter-select" className="h-12 border-zinc-200 rounded-none bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {STATUS_OPTIONS.map((s) => (<SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Materials list */}
      {filtered.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-14 text-center">
          <Package size={44} className="mx-auto text-zinc-300" weight="duotone" />
          <div className="mt-3 text-zinc-500 text-sm">No materials match your filter.</div>
        </div>
      ) : (
        <div className="grid gap-3" data-testid="orders-list">
          {filtered.map((o) => (
            <Link
              key={o.order_id}
              to={`/orders/${o.order_id}`}
              data-testid={`order-card-${o.order_number}`}
              className="group bg-white border border-zinc-200 hover:border-zinc-900 p-4 md:p-5 transition-all duration-200 hover:-translate-y-0.5"
            >
              <div className="flex items-center gap-4">
                <OrderThumb order={o} className="w-20 h-20 md:w-24 md:h-24 shrink-0" />
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:items-center flex-1 min-w-0">
                  <div className="md:col-span-5 min-w-0">
                    <div className="mt-tiny text-zinc-400 font-mono-mt">{o.order_number}</div>
                    <div className="font-display text-lg font-bold text-zinc-900 mt-1 group-hover:text-[#0047AB] transition-colors truncate">
                      {o.product_name}
                    </div>
                    <div className="mt-1 text-xs text-zinc-500">{o.product_type} · Qty {o.quantity}</div>
                  </div>
                  <div className="md:col-span-3"><StatusChip status={o.current_status} /></div>
                  <div className="md:col-span-2">
                    <div className="mt-tiny text-zinc-400">Expected</div>
                    <div className="text-sm font-medium text-zinc-900">{formatDate(o.expected_delivery)}</div>
                  </div>
                  <div className="md:col-span-2 flex md:justify-end items-center gap-2">
                    <div className="font-mono-mt text-sm font-semibold text-zinc-900">{formatCurrency(o.total_price)}</div>
                    <ArrowUpRight size={18} className="text-zinc-400 group-hover:text-[#FF5A1F] transition-colors" />
                  </div>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-zinc-100 flex justify-end">
                <ShareLinkButton
                  url={trackingUrl(o.order_id)}
                  idleLabel="Share tracking link"
                  copiedLabel="Tracking link copied"
                  toastLabel="Public tracking link copied"
                  testId={`share-tracking-${o.order_number}`}
                />
              </div>
            </Link>
          ))}
        </div>
      )}
    </ClientShell>
  );
}

// Reusable product thumbnail — pulls from linked catalog product image with
// category-coloured placeholder fallback.
export function OrderThumb({ order, className = "w-20 h-20" }) {
  const { byId } = useCategories();
  const cat = byId(order.category_id);
  const blob = cat?.blob || "bg-zinc-100";
  const accent = cat?.accent_fg || "text-zinc-500";
  return (
    <div
      className={`${className} border border-zinc-200 overflow-hidden ${blob} grid place-items-center relative`}
      data-testid={`order-thumb-${order.order_number}`}
    >
      {order.product_image_path ? (
        <AuthImage
          path={order.product_image_path}
          alt={order.product_name}
          className="absolute inset-0 w-full h-full object-cover"
          fallback={<Package size={28} weight="duotone" className={accent} />}
        />
      ) : (
        <Package size={28} weight="duotone" className={accent} />
      )}
    </div>
  );
}
